import { isMainnet } from "@/env";
import { Logger } from "@/utils/logger";

const EVM_MAINNET_CHAINS = {
  optionalChains: [1, 42161, 8453] as [number, ...number[]], // Ethereum, Arbitrum, Base (all optional)
};
const EVM_TESTNET_CHAINS = {
  optionalChains: [11155111, 421614, 84532] as [number, ...number[]], // Sepolia, Arbitrum Sepolia, Base Sepolia (all optional)
};

const WALLETCONNECT_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 480 480'%3E%3Crect width='480' height='480' rx='240' fill='%233396FF'/%3E%3Cpath d='M141.1 185.2c54.6-53.5 143.2-53.5 197.8 0l6.6 6.4a6.7 6.7 0 0 1 0 9.7l-22.5 22a3.5 3.5 0 0 1-4.9 0l-9-8.9c-38.1-37.3-99.9-37.3-138 0l-9.7 9.5a3.5 3.5 0 0 1-4.9 0l-22.5-22a6.7 6.7 0 0 1 0-9.7l7.1-7Zm244.5 45.5 20 19.7a6.7 6.7 0 0 1 0 9.6l-90.3 88.4a7 7 0 0 1-9.9 0l-64.1-62.8a1.8 1.8 0 0 0-2.5 0l-64.1 62.8a7 7 0 0 1-9.9 0l-90.3-88.4a6.7 6.7 0 0 1 0-9.6l20-19.7a7 7 0 0 1 9.9 0l64.1 62.8a1.8 1.8 0 0 0 2.5 0l64.1-62.8a7 7 0 0 1 9.9 0l64.1 62.8a1.8 1.8 0 0 0 2.5 0l64.1-62.8a7 7 0 0 1 9.9 0Z' fill='%23fff'/%3E%3C/svg%3E" as `data:image/${string}`;

type EthereumProvider = Awaited<
  ReturnType<typeof import("@walletconnect/ethereum-provider").default.init>
>;

let wcEvmProvider: EthereumProvider | null = null;
let initPromise: Promise<EthereumProvider> | null = null;
let disconnecting: Promise<void> | null = null;

/**
 * Stable EIP-1193 object announced via EIP-6963.
 *
 * setupAutomaticEthereumWalletDerivation never unregisters old derived
 * wallets when a new announcement with the same rdns arrives. So after
 * disconnect → reconnect, the adapter's connect("WalletConnect (Ethereum)")
 * finds the OLD derived wallet — which still holds a reference to whatever
 * provider object was in the first announcement.
 *
 * By always announcing this same object (whose methods delegate to the
 * current `wcEvmProvider`), even stale derived wallets route requests
 * through the latest live provider.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const stableProvider: any = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: async (args: { method: string; params?: any[] }): Promise<any> => {
    if (!wcEvmProvider) throw new Error("WalletConnect not connected");
    const result = await wcEvmProvider.request(args);
    // WC EthereumProvider may return eth_chainId as a decimal number;
    // EIP-1193 requires a hex string. Normalize to prevent double-encoding
    // in downstream SDKs (e.g. cross-chain-core interprets 42161 as 0x42161 = 270689).
    if (args.method === "eth_chainId" && typeof result === "number") {
      return `0x${result.toString(16)}`;
    }
    return result;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on: (event: any, listener: (...args: any[]) => void) => {
    wcEvmProvider?.on(event, listener);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return stableProvider;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  removeListener: (event: any, listener: (...args: any[]) => void) => {
    wcEvmProvider?.removeListener(event, listener);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return stableProvider;
  },
};

/** Lazily initialize (or return the cached) WalletConnect EthereumProvider. */
async function getOrInitProvider(projectId: string): Promise<EthereumProvider> {
  if (wcEvmProvider) return wcEvmProvider;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const { default: EthereumProvider } = await import("@walletconnect/ethereum-provider");
      const { optionalChains } = isMainnet ? EVM_MAINNET_CHAINS : EVM_TESTNET_CHAINS;

      const opts = {
        projectId,
        optionalChains,
        showQrModal: false,
        methods: [
          "eth_requestAccounts",
          "eth_accounts",
          "personal_sign",
          "eth_sendTransaction",
          "eth_chainId",
        ],
        events: ["accountsChanged", "chainChanged"],
        metadata: {
          name: "Decibel",
          description: "Decibel Perpetual Futures DEX",
          url: typeof window !== "undefined" ? window.location.origin : "https://decibel.trade",
          icons: ["https://decibel.trade/favicon.ico"],
        },
      };

      let provider: Awaited<ReturnType<typeof EthereumProvider.init>>;
      try {
        provider = await EthereumProvider.init(opts);
      } catch {
        clearWcLocalStorage();
        provider = await EthereumProvider.init(opts);
      }

      wcEvmProvider = provider;
      return provider;
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
}

/** Emit an EIP-6963 announcement so the Aptos derived-wallet adapter discovers this provider. */
function announceProvider(): void {
  if (typeof window === "undefined") return;

  const detail = Object.freeze({
    info: Object.freeze({
      uuid: crypto.randomUUID(),
      name: "WalletConnect",
      rdns: "com.walletconnect",
      icon: WALLETCONNECT_ICON,
    }),
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    provider: stableProvider,
  });

  window.dispatchEvent(new CustomEvent("eip6963:announceProvider", { detail }));
}

/**
 * Open a WC session. The caller receives the pairing URI via `onDisplayUri`
 * and is responsible for showing a QR code. The promise resolves once the
 * remote wallet approves the session.
 */
export async function connectWcEvm(
  projectId: string,
  onDisplayUri: (uri: string) => void,
): Promise<void> {
  if (disconnecting) await disconnecting;

  const provider = await getOrInitProvider(projectId);

  provider.on("display_uri", onDisplayUri);
  try {
    await provider.connect();
  } finally {
    provider.removeListener("display_uri", onDisplayUri);
  }

  announceProvider();
}

/**
 * Restore a previously established WalletConnect EVM session from storage.
 * Called on page load to enable auto-reconnect without showing a QR code.
 */
export async function restoreWcEvmSession(projectId: string): Promise<boolean> {
  try {
    const provider = await getOrInitProvider(projectId);
    if (!provider.session) return false;
    announceProvider();
    return true;
  } catch (error) {
    Logger.error(error, {
      message: "Failed to restore WalletConnect EVM session",
    });
    return false;
  }
}

/** Disconnect the WalletConnect EVM session and clear local provider state. */
export function disconnectWcEvm(): void {
  const provider = wcEvmProvider;
  if (!provider) return;

  wcEvmProvider = null;
  initPromise = null;

  // universal-provider throws "Please call connect() before enable()" when there is
  // no active session. Auto-restore initializes the provider even when no session
  // exists, so a user-clicked disconnect would throw.
  if (!provider.session) return;

  disconnecting = provider
    .disconnect()
    .catch((e: unknown) => {
      Logger.error(e, { message: "Failed to disconnect WalletConnect EVM" });
    })
    .finally(() => {
      disconnecting = null;
    });
}

/** Clear all WalletConnect-related localStorage keys as a recovery mechanism. */
function clearWcLocalStorage(): void {
  if (typeof window === "undefined") return;
  for (const key of Object.keys(localStorage)) {
    if (
      key.startsWith("wc@") ||
      key.includes("walletconnect") ||
      key.startsWith("W3M") ||
      key.includes("appkit")
    ) {
      localStorage.removeItem(key);
    }
  }
}
